import utils from '@/util'
import { ACQUISITION_COOKIE, ACQUISITION_FLAG, SIGNAL_TTL_DAYS } from '@/constant/acquisitionSignal.constant'

// Re-exported so existing importers of segment.utils keep working unchanged.
export { ACQUISITION_COOKIE, ACQUISITION_FLAG, SIGNAL_TTL_DAYS }

const TTL_MS = SIGNAL_TTL_DAYS * 24 * 60 * 60 * 1000
const SIGNAL_TYPES = ['page', 'ai-prompt', 'ob-answer']
const MAX_SIGNALS = 5
const MAX_VALUE_LENGTH = 500

// Shared normalization for cookie and persisted signal lists: allowlist
// rebuild, TTL prune, oldest-first sort, cap. Persisted logs go through the
// same rules as the cookie so the 14-day expiry holds on both sides.
const normalizeSignals = (signals, now) => {
  if (!Array.isArray(signals)) return []
  const cutoff = now.getTime() - TTL_MS
  return signals
    .map((signal) => {
      if (!signal || typeof signal !== 'object') return null
      const at = Date.parse(signal.at)
      if (Number.isNaN(at) || at < cutoff) return null
      if (!SIGNAL_TYPES.includes(signal.t)) return null
      if (typeof signal.v !== 'string') return null
      // Rebuild as a fresh allowlisted literal so attacker-controlled extra
      // keys (including a literal "__proto__" own key from JSON.parse) never
      // survive into the persisted log.
      return { t: signal.t, v: signal.v.slice(0, MAX_VALUE_LENGTH), at: signal.at }
    })
    .filter(Boolean)
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .slice(0, MAX_SIGNALS)
}

const normalizeLog = (log, now) => {
  if (!log || typeof log !== 'object' || log.v !== 1) return null
  const signals = normalizeSignals(log.signals, now)
  if (!signals.length) return null
  return { v: 1, signals }
}

export const parseAcquisitionLog = (raw, now = new Date()) => {
  if (!raw) return null
  let parsed
  try {
    parsed = JSON.parse(decodeURIComponent(raw))
  } catch (error) {
    return null
  }
  return normalizeLog(parsed, now)
}

// Raw document.cookie read on purpose: services/cookie getValue applies the
// eu- region prefix and true/false coercion, but ahaSegment is written
// unprefixed by landing-site GTM.
export const readAcquisitionCookie = (now = new Date()) => parseAcquisitionLog(utils.getCookie(ACQUISITION_COOKIE), now)

export const getAcquisitionLog = (currentUser = null, now = new Date()) => {
  const persisted = normalizeLog(currentUser?.personalInformation?.acquisition, now)
  if (persisted) return persisted
  return readAcquisitionCookie(now)
}

// Union of the persisted log and the cookie log: TTL-pruned, deduped by
// (type, value) keeping the earliest occurrence (first touch wins), sorted
// oldest-first, capped. Returns null when nothing survives. Used by the
// acquisition flush so a profile frozen on early junk converges instead of
// keeping its first snapshot forever (AHA-47419).
export const mergeAcquisitionLogs = (persistedLog, cookieLog, now = new Date()) => {
  const combined = [
    ...(normalizeLog(persistedLog, now)?.signals || []),
    ...(normalizeLog(cookieLog, now)?.signals || []),
  ]
  const byTypeAndValue = new Map()
  combined.forEach((signal) => {
    const key = `${signal.t} ${signal.v}`
    const existing = byTypeAndValue.get(key)
    if (!existing || Date.parse(signal.at) < Date.parse(existing.at)) byTypeAndValue.set(key, signal)
  })
  const signals = [...byTypeAndValue.values()]
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .slice(0, MAX_SIGNALS)
  if (!signals.length) return null
  return { v: 1, signals }
}
